import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import * as React from "react";

const inputVariants = cva(
  "flex gap-2 w-full placeholder:font-medium border border-input text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-within:brand-focus transition-[border-color,box-shadow] items-center",
  {
    variants: {
      sizeVariant: {
        default: "h-10 px-3 text-sm rounded-xl",
        sm: "h-8 px-2 text-sm rounded-listen-8",
        xs: "h-7 px-2 text-sm focus-visible:ring-offset-0 rounded-listen-8",
      },
    },
    defaultVariants: {
      sizeVariant: "default",
    },
  }
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  inputClassName?: string;
  error?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputClassName,
      type,
      sizeVariant = "default",
      iconLeft,
      iconRight,
      error,
      ...props
    },
    ref
  ) => (
    <div
      className={cn(
        inputVariants({ sizeVariant, className }),
        error &&
          "border-content-negative focus-within:border-content-negative focus-within:shadow-[0_0_0_2px_hsl(var(--content-negative)_/_0.12)]",
        {
          "cursor-not-allowed opacity-50": props.disabled,
        }
      )}
    >
      {iconLeft && (
        <Slot className={cn("h-4 w-4 text-muted-foreground/50 shrink-0")}>
          {iconLeft}
        </Slot>
      )}
      <input
        type={type}
        className={cn(
          "bg-transparent h-full focus:outline-none w-full disabled:cursor-not-allowed",
          inputClassName
        )}
        ref={ref}
        {...props}
        aria-invalid={error || props["aria-invalid"]}
      />
      {iconRight && (
        <div
          className={cn(
            "h-4 w-4 text-muted-foreground/50 shrink-0 flex items-center justify-center"
          )}
        >
          {iconRight}
        </div>
      )}
    </div>
  )
);
Input.displayName = "Input";

const InputWithShortcut = ({
  shortcutKey,
  ...props
}: InputProps & { shortcutKey: string }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isMac, setIsMac] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for both macOS (metaKey) and Windows (ctrlKey)
      const isMacCommand = event.metaKey;
      const isWindowsCtrl = event.ctrlKey && !event.altKey;

      if (
        (isMacCommand || isWindowsCtrl) &&
        event.key.toLowerCase() === shortcutKey.toLowerCase()
      ) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shortcutKey]);

  React.useEffect(() => {
    const isMac =
      typeof window !== "undefined" &&
      /Mac|iPod|iPhone|iPad/.test(window.navigator.userAgent);
    setIsMac(isMac);
  }, []);

  return (
    <div className="relative group">
      <Input {...props} ref={inputRef} />
      {isMac !== undefined && (
        <div
          className={clsx(
            "pointer-events-none absolute right-[0.75rem] top-0 h-full select-none flex items-center group-focus-within:opacity-0",
            // for type search the browser shows an x on hover
            props.type === "search" && !!props.value && "group-hover:opacity-0",
            (props.value?.toString().length ?? 0) > 5 && "opacity-0"
          )}
        >
          <kbd
            className="font-mono text-[11px] leading-8 text-neutral-500 font-semibold flex items-center gap-1"
            suppressHydrationWarning
          >
            {isMac ? (
              <span className="text-[12.5px]">⌘</span>
            ) : (
              <span className="text-[11px]">Ctrl</span>
            )}
            {shortcutKey.toUpperCase()}
          </kbd>
        </div>
      )}
    </div>
  );
};

InputWithShortcut.displayName = "InputWithShortcut";

export { Input, InputWithShortcut };
